/**
 * useImageModels — fetches entitlement-filtered image models from the backend.
 */

import { useCallback, useEffect, useState } from 'react'

import {
  getImageModels,
  type ByomModelInfo,
  type ImageModelsResponse,
} from '../api/client'
import { useAuthOptional } from '../contexts/authContextRaw'
import { createNadDebug } from '../utils/debugLog'

const log = createNadDebug('image-models')

interface UseImageModelsResult {
  native: string[]
  byom: ByomModelInfo[]
  defaultModel: string | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

const EMPTY_RESPONSE: ImageModelsResponse = {
  native: [],
  byom: [],
  default_model: null,
}
interface ImageModelCacheEntry {
  response: ImageModelsResponse
  version: number
  fetchedAtMs: number
}

interface ImageModelRequestEntry {
  request: Promise<ImageModelsResponse>
  version: number
}

type ImageModelLoader = () => Promise<ImageModelsResponse>

const cachedResponses = new Map<string, ImageModelCacheEntry>()
export const IMAGE_MODELS_CACHE_TTL_MS = 30_000
let gatewayConfigVersion = 0
const inFlightRequests = new Map<string, ImageModelRequestEntry>()
const listeners = new Set<() => void>()
const cacheExpiryTimers = new Map<string, ReturnType<typeof setTimeout>>()

function notifyListenersOfModelChange(): void {
  gatewayConfigVersion++
  for (const listener of listeners) listener()
}

function clearCacheExpiryTimers(): void {
  for (const timer of cacheExpiryTimers.values()) clearTimeout(timer)
  cacheExpiryTimers.clear()
}

function scheduleCacheExpiry(accountScope: string, fetchedAtMs: number): void {
  const existing = cacheExpiryTimers.get(accountScope)
  if (existing) clearTimeout(existing)
  const timer = setTimeout(() => {
    const cached = cachedResponses.get(accountScope)
    cacheExpiryTimers.delete(accountScope)
    if (cached?.fetchedAtMs !== fetchedAtMs) return
    cachedResponses.delete(accountScope)
    notifyListenersOfModelChange()
  }, IMAGE_MODELS_CACHE_TTL_MS)
  cacheExpiryTimers.set(accountScope, timer)
}

export function notifyImageModelConfigChanged(): void {
  clearCacheExpiryTimers()
  notifyListenersOfModelChange()
}

/** @internal — reset module-level cache for tests. */
export function _resetImageModelCacheForTests(): void {
  cachedResponses.clear()
  gatewayConfigVersion = 0
  inFlightRequests.clear()
  clearCacheExpiryTimers()
  listeners.clear()
}

function requestImageModels(
  accountScope: string,
  version: number,
  loader: ImageModelLoader = getImageModels,
): Promise<ImageModelsResponse> {
  const cached = cachedResponses.get(accountScope)
  if (cached?.version === version && Date.now() - cached.fetchedAtMs < IMAGE_MODELS_CACHE_TTL_MS) {
    return Promise.resolve(cached.response)
  }
  const inFlight = inFlightRequests.get(accountScope)
  if (inFlight?.version === version) return inFlight.request

  log({ event: 'fetch:start', trace_id: 'image-models:fetch', cache_warm: Boolean(cached), account_scope: accountScope })
  const request = loader()
    .then((result) => {
      if (version !== gatewayConfigVersion) {
        log({
          event: 'fetch:stale',
          trace_id: 'image-models:fetch',
          request_version: version,
          current_version: gatewayConfigVersion,
        })
        return result
      }
      log({
        event: 'fetch:ok',
        trace_id: 'image-models:fetch',
        native_count: result.native.length,
        byom_count: result.byom?.length ?? 0,
        default_model: result.default_model,
      })
      const fetchedAtMs = Date.now()
      cachedResponses.set(accountScope, { response: result, version, fetchedAtMs })
      scheduleCacheExpiry(accountScope, fetchedAtMs)
      return result
    })
    .catch((err) => {
      log({
        event: 'fetch:error',
        trace_id: 'image-models:fetch',
        error: err instanceof Error ? err.message : String(err),
      })
      throw err
    })
    .finally(() => {
      if (inFlightRequests.get(accountScope)?.request === request) {
        inFlightRequests.delete(accountScope)
      }
    })
  inFlightRequests.set(accountScope, { request, version })
  return request
}

/** @internal — exercise shared request/cache behavior without React or module mocks. */
export function _requestImageModelsForTests(
  accountScope: string,
  version: number,
  loader: ImageModelLoader,
): Promise<ImageModelsResponse> {
  return requestImageModels(accountScope, version, loader)
}

export function useImageModels(): UseImageModelsResult {
  const auth = useAuthOptional()
  const accountScope = auth?.user?.id ?? 'anonymous'
  const authIsLoading = auth?.isLoading ?? false
  const initialCache = cachedResponses.get(accountScope)
  const initialCacheFresh = Boolean(
    !authIsLoading
    && initialCache
    && initialCache.version === gatewayConfigVersion
    && Date.now() - initialCache.fetchedAtMs < IMAGE_MODELS_CACHE_TTL_MS,
  )
  const [data, setData] = useState<ImageModelsResponse>(
    initialCacheFresh && initialCache ? initialCache.response : EMPTY_RESPONSE,
  )
  const [dataScope, setDataScope] = useState(accountScope)
  const [isLoading, setIsLoading] = useState(!initialCacheFresh)
  const [error, setError] = useState<string | null>(null)
  const [version, setVersion] = useState(gatewayConfigVersion)

  const refetch = useCallback(() => {
    notifyImageModelConfigChanged()
  }, [])

  useEffect(() => {
    const handler = () => setVersion(gatewayConfigVersion)
    listeners.add(handler)
    return () => {
      listeners.delete(handler)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    if (authIsLoading) {
      setData(EMPTY_RESPONSE)
      setDataScope(accountScope)
      setIsLoading(true)
      setError(null)
      return () => {
        cancelled = true
      }
    }
    const cached = cachedResponses.get(accountScope)
    if (
      cached?.version === version
      && Date.now() - cached.fetchedAtMs < IMAGE_MODELS_CACHE_TTL_MS
    ) {
      setData(cached.response)
      setDataScope(accountScope)
      setIsLoading(false)
      setError(null)
      return () => {
        cancelled = true
      }
    }
    setData(EMPTY_RESPONSE)
    setDataScope(accountScope)
    setIsLoading(true)
    setError(null)

    requestImageModels(accountScope, version)
      .then((result) => {
        if (!cancelled) {
          setData(result)
          setDataScope(accountScope)
          setIsLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err))
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [accountScope, authIsLoading, version])

  const scopedData = !authIsLoading && dataScope === accountScope ? data : EMPTY_RESPONSE
  const scopeChanged = dataScope !== accountScope

  return {
    native: scopedData.native,
    byom: scopedData.byom ?? [],
    defaultModel: scopedData.default_model ?? null,
    isLoading: authIsLoading || scopeChanged || isLoading,
    error: authIsLoading || scopeChanged ? null : error,
    refetch,
  }
}
